// client/src/pages/School_Admin/Finance/FeeStructureManagerPage.js
import  { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Chip,
  Snackbar,
  Alert,
  FormControlLabel,
  Checkbox,
  FormHelperText
} from '@mui/material';
import {fetchJson} from '../../../utils/fetchJson';
import AppLoader from '../../../components/AppLoader';


const FeeStructureManagerPage = () => {
  const { t } = useTranslation();
  // Context-aware cache keys
  const schoolId = typeof window !== 'undefined' ? localStorage.getItem('schoolId') : null;
  const phase = typeof window !== 'undefined' ? (localStorage.getItem('schoolAdmin_activePhase') || '') : '';
  const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
  const ayId = typeof window !== 'undefined' ? (localStorage.getItem(ayKey) || localStorage.getItem('currentAcademicYearId') || '') : '';
  const cacheKeyStructures = `finance:feeStructures:${schoolId || 'ns'}|${phase || 'ns'}|${ayId || 'ns'}`;
  const cacheKeyClasses = `finance:classes:${schoolId || 'ns'}|${phase || 'ns'}|${ayId || 'ns'}`;
  const cacheKeyFeeTypes = `finance:feeTypes:${schoolId || 'ns'}|${phase || 'ns'}|${ayId || 'ns'}`;

  // Seed from cache if present
  const cachedStructures = (() => { try { return JSON.parse(localStorage.getItem(cacheKeyStructures) || '[]'); } catch { return []; } })();
  const cachedClasses = (() => { try { return JSON.parse(localStorage.getItem(cacheKeyClasses) || '[]'); } catch { return []; } })();
  const cachedFeeTypes = (() => { try { return JSON.parse(localStorage.getItem(cacheKeyFeeTypes) || '[]'); } catch { return []; } })();

  const [feeStructures, setFeeStructures] = useState(Array.isArray(cachedStructures) ? cachedStructures : []);
  const [classes, setClasses] = useState(Array.isArray(cachedClasses) ? cachedClasses : []);
  const [feeTypes, setFeeTypes] = useState(Array.isArray(cachedFeeTypes) ? cachedFeeTypes : []);
  const [loading, setLoading] = useState(true);
  const guardRef = useRef(null);
  const [showForm, setShowForm] = useState(false);
  const [editingStructure, setEditingStructure] = useState(null);
  const [formData, setFormData] = useState({
    description: '',
    classIds: [],
    feeTypeId: '',
    academicYearId: ayId || '',
    phaseId: localStorage.getItem('phaseId') || '1',  // Add phaseId
    amount: '',
    dueDate: '',
    lateFee: '',
    discount: '',
    isCompulsory: false,
    installment: false,
    installmentCount: 1,
    status: 'active'
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [academicYears, setAcademicYears] = useState([]);
  const [currentAcademicYear, setCurrentAcademicYear] = useState(null);

  const [printClassId, setPrintClassId] = useState('');

  // Currency formatter for XAF (Cameroon)
  const formatXAF = (amount) => new Intl.NumberFormat('en-CM', {
    style: 'currency',
    currency: 'XAF',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(Number(amount || 0));

  // Installments dialog state
  const [installmentsOpen, setInstallmentsOpen] = useState(false);
  const [activeStructure, setActiveStructure] = useState(null);
  const [installments, setInstallments] = useState([]);
  const [instForm, setInstForm] = useState({ name: '', sequence: 1, amount: '', dueAt: '', isMandatory: true });
  const [genCount, setGenCount] = useState(2);
  const [instCount, setInstCount] = useState(1);
  const [originalInstallments, setOriginalInstallments] = useState([]);
  // Inline installments (inside main form when editing an existing structure)
  const [inlineInstallments, setInlineInstallments] = useState([]);
  const [inlineOriginalInstallments, setInlineOriginalInstallments] = useState([]);
  const [inlineInstCount, setInlineInstCount] = useState(1);

  // Auto-select first fee type when dialog opens and none selected yet
  useEffect(() => {
    if (!showForm) return;
    setFormData((prev) => {
      let updated = { ...prev };
      // Auto-select first fee type if none selected
      if (!updated.feeTypeId && Array.isArray(feeTypes) && feeTypes.length > 0) {
        updated.feeTypeId = feeTypes[0].id;
      }
      // Don't auto-select classes - let user choose which classes they want
      // Auto-select academic year if none selected
      if (!updated.academicYearId && (currentAcademicYear?.id || ayId)) {
        updated.academicYearId = currentAcademicYear?.id || ayId;
      }
      return updated;
    });
  }, [showForm, feeTypes, classes, editingStructure, currentAcademicYear, ayId]);

  // Seed inline installments for Create flow when enabling installments
  useEffect(() => {
    if (!showForm) return;
    if (editingStructure) return; // edit path handled in handleEdit
    if (!formData.installment) {
      setInlineInstallments([]);
      setInlineOriginalInstallments([]);
      setInlineInstCount(1);
      return;
    }
    // If enabling installments in Create, ensure at least 1 row
    if (inlineInstallments.length === 0) {
      const seeded = [{ id: 'temp-1', name: t('feeStructure.installments.defaultName', { n: 1 }), sequence: 1, amount: 0, dueAt: '', isMandatory: true }];
      setInlineInstallments(seeded);
      setInlineOriginalInstallments([]);
      setInlineInstCount(1);
    }
  }, [showForm, formData.installment]);

  // Keep inlineInstCount in sync with the form field and adjust rows accordingly in Create flow
  useEffect(() => {
    if (!showForm) return;
    if (!formData.installment) return;
    const n = Math.max(1, Number(formData.installmentCount || 1));
    if (inlineInstCount !== n) {
      setInlineInstCount(n);
      // Resize rows
      setInlineInstallments(prev => {
        const current = prev.length;
        let next = [...prev]
          .sort((a, b) => Number(a.sequence) - Number(b.sequence))
          .map((x, idx) => ({ ...x, sequence: idx + 1 }));
        if (n > current) {
          const start = current + 1;
          const additions = Array.from({ length: n - current }, (_, i) => ({
            id: `temp-${start + i}`,
            name: t('feeStructure.installments.defaultName', { n: start + i }),
            sequence: start + i,
            amount: 0,
            dueAt: '',
            isMandatory: true
          }));
          next = [...next, ...additions];
        } else if (n < current) {
          next = next.slice(0, n);
        }
        return next.map((x, idx) => ({ ...x, sequence: idx + 1 }));
      });
    }
  }, [showForm, formData.installment, formData.installmentCount]);

  // ============ Installment Handlers ============
  const openInstallments = async (structure) => {
    setActiveStructure(structure);
    setInstallmentsOpen(true);
    try {
      const res = await fetchJson(`/api/finance/fee-structures/${structure.id}/installments`, { method: 'GET' });
      const list = res.data || res.installments || [];
      if ((list || []).length === 0) {
        // Seed one local row so the user sees a form immediately
        const seeded = [{ id: 'temp-1', name: t('feeStructure.installments.defaultName', { n: 1 }), sequence: 1, amount: 0, dueAt: '', isMandatory: true }];
        setInstallments(seeded);
        setOriginalInstallments([]);
        setInstCount(1);
      } else {
        setInstallments(list);
        setOriginalInstallments(list);
        setInstCount(Math.max(1, list.length || 1));
      }
    } catch (error) {
      setSnackbar({ open: true, message: t('feeStructure.errors.loadInstallments'), severity: 'error' });
    }
  };

  // ===== Inline Installments (inside main form while editing) =====
  const handleInlineInstCountChange = (value) => {
    const desired = Math.max(1, Number(value || 1));
    setInlineInstCount(desired);
    setInlineInstallments(prev => {
      const current = prev.length;
      let next = [...prev]
        .sort((a, b) => Number(a.sequence) - Number(b.sequence))
        .map((x, idx) => ({ ...x, sequence: idx + 1 }));
      if (desired > current) {
        const start = current + 1;
        const additions = Array.from({ length: desired - current }, (_, i) => ({
          id: `temp-${start + i}`,
          name: t('feeStructure.installments.defaultName', { n: start + i }),
          sequence: start + i,
          amount: 0,
          dueAt: '',
          isMandatory: true
        }));
        next = [...next, ...additions];
      } else if (desired < current) {
        next = next.slice(0, desired);
      }
      return next.map((x, idx) => ({ ...x, sequence: idx + 1 }));
    });
  };

  const inlineAutoDistributeDueDates = () => {
    if (!editingStructure) return;
    const overallStr = formData.dueDate || editingStructure.dueDate;
    if (!overallStr) {
      setSnackbar({ open: true, message: t('feeStructure.errors.setDueDate'), severity: 'warning' });
      return;
    }
    const overall = new Date(overallStr);
    const addMonths = (date, months) => {
      const d = new Date(date);
      const day = d.getDate();
      d.setMonth(d.getMonth() + months);
      if (d.getDate() < day) d.setDate(0);
      return d;
    };
    setInlineInstallments(prev => {
      const arr = [...prev].sort((a, b) => Number(a.sequence) - Number(b.sequence));
      const n = arr.length;
      return arr.map((inst, idx) => {
        const monthsBack = (n - 1) - idx;
        const dt = addMonths(overall, -monthsBack);
        const iso = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate(), 9, 0, 0).toISOString().slice(0, 16);
        return { ...inst, dueAt: iso };
      });
    });
  };

  const inlineRebalance = () => {
    if (!editingStructure) return;
    const total = Number(formData.amount || editingStructure.amount || 0);
    setInlineInstallments(prev => {
      const n = prev.length;
      if (n === 0) return prev;
      const amounts = distributeEqual(total, n);
      return prev
        .sort((a, b) => Number(a.sequence) - Number(b.sequence))
        .map((inst, idx) => ({ ...inst, sequence: idx + 1, amount: amounts[idx] }));
    });
  };

  const inlineSaveAll = async () => {
    if (!editingStructure) return;
    // Validate total matches
    const sum = sumInstallments(inlineInstallments);
    const total = Number(formData.amount || editingStructure.amount || 0);
    if (Math.abs(sum - total) >= 0.01) {
      setSnackbar({ open: true, message: t('feeStructure.errors.installmentsMismatchSave'), severity: 'warning' });
      return;
    }
    try {
      const desired = inlineInstallments
        .slice()
        .sort((a, b) => Number(a.sequence) - Number(b.sequence))
        .map((x, idx) => ({ ...x, sequence: idx + 1 }));
      const toCreate = desired.filter(i => !i.id || String(i.id).startsWith('temp')).map(i => ({
        name: i.name,
        sequence: i.sequence,
        amount: Number(i.amount || 0),
        dueAt: i.dueAt || '',
        isMandatory: !!i.isMandatory
      }));
      const toUpdate = desired.filter(i => i.id && !String(i.id).startsWith('temp')).map(i => ({ id: i.id, payload: {
        name: i.name,
        sequence: i.sequence,
        amount: Number(i.amount || 0),
        dueAt: i.dueAt || '',
        isMandatory: !!i.isMandatory
      }}));
      const toDelete = (inlineOriginalInstallments || []).filter(oi => !desired.some(i => i.id === oi.id));

      if (toCreate.length > 0) {
        await fetchJson(`/api/finance/fee-structures/${editingStructure.id}/installments`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: toCreate })
        });
      }
      await Promise.all(toUpdate.map(u => fetchJson(`/api/finance/fee-structures/${editingStructure.id}/installments/${u.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(u.payload)
      })));
      await Promise.all(toDelete.map(d => fetchJson(`/api/finance/fee-structures/${editingStructure.id}/installments/${d.id}`, { method: 'DELETE' })));

      const res = await fetchJson(`/api/finance/fee-structures/${editingStructure.id}/installments`, { method: 'GET' });
      const list = res.data || res.installments || [];
      setInlineInstallments(list);
      setInlineOriginalInstallments(list);
      setInlineInstCount(Math.max(1, list.length || 1));
      setSnackbar({ open: true, message: t('feeStructure.snack.installmentsSaved'), severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: e?.message || t('feeStructure.errors.saveInstallments'), severity: 'error' });
    }
  };

  // Instantly adjust local rows when count changes (no apply step)
  const handleInstCountChange = (value) => {
    const desired = Math.max(1, Number(value || 1));
    setInstCount(desired);
    setInstallments(prev => {
      const current = prev.length;
      let next = [...prev]
        .sort((a, b) => Number(a.sequence) - Number(b.sequence))
        .map((x, idx) => ({ ...x, sequence: idx + 1 }));
      if (desired > current) {
        const start = current + 1;
        const additions = Array.from({ length: desired - current }, (_, i) => ({
          id: `temp-${start + i}`,
          name: t('feeStructure.installments.defaultName', { n: start + i }),
          sequence: start + i,
          amount: 0,
          dueAt: '',
          isMandatory: true
        }));
        next = [...next, ...additions];
      } else if (desired < current) {
        next = next.slice(0, desired);
      }
      // normalize sequences 1..n
      next = next.map((x, idx) => ({ ...x, sequence: idx + 1 }));
      return next;
    });
  };

  // Auto-distribute due dates up to overall due date (monthly intervals ending at overall due date)
  const autoDistributeDueDates = () => {
    if (!activeStructure) return;
    if (!activeStructure.dueDate) {
      setSnackbar({ open: true, message: t('feeStructure.errors.overallDueDateMissing'), severity: 'warning' });
      return;
    }
    const overall = new Date(activeStructure.dueDate);
    const addMonths = (date, months) => {
      const d = new Date(date);
      const day = d.getDate();
      d.setMonth(d.getMonth() + months);
      if (d.getDate() < day) d.setDate(0);
      return d;
    };
    setInstallments(prev => {
      const arr = [...prev].sort((a, b) => Number(a.sequence) - Number(b.sequence));
      const n = arr.length;
      const next = arr.map((inst, idx) => {
        const monthsBack = (n - 1) - idx;
        const dt = addMonths(overall, -monthsBack);
        const iso = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate(), 9, 0, 0).toISOString().slice(0, 16);
        return { ...inst, dueAt: iso };
      });
      return next;
    });
    setSnackbar({ open: true, message: t('feeStructure.snack.dueDatesDistributed'), severity: 'info' });
  };

  // Prevent closing when totals mismatch
  const handleCloseInstallments = () => {
    if (activeStructure) {
      const sum = sumInstallments(installments);
      const total = Number(activeStructure.amount || 0);
      if (Math.abs(sum - total) >= 0.01) {
        setSnackbar({ open: true, message: t('feeStructure.errors.installmentsTotalMismatch'), severity: 'warning' });
        return;
      }
    }
    setInstallmentsOpen(false);
  };

  // Removed legacy seeding; fee types should be managed from the Fee Types page and fetched from API

  const addInstallment = async () => {
    if (!activeStructure) return;
    try {
      const item = {
        name: (instForm.name || '').trim() || t('feeStructure.installments.defaultName', { n: instForm.sequence }),
        sequence: Number(instForm.sequence),
        amount: Number(instForm.amount || 0),
        dueAt: instForm.dueAt,
        isMandatory: !!instForm.isMandatory
      };
      await fetchJson(`/api/finance/fee-structures/${activeStructure.id}/installments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [item] })
      });
      setSnackbar({ open: true, message: t('feeStructure.snack.installmentAdded'), severity: 'success' });
      // reload
      const res = await fetchJson(`/api/finance/fee-structures/${activeStructure.id}/installments`, { method: 'GET' });
      setInstallments(res.data || res.installments || []);
      setInstForm({ name: '', sequence: 1, amount: '', dueAt: '', isMandatory: true });
    } catch (error) {
      setSnackbar({ open: true, message: error?.message || t('feeStructure.errors.addInstallment'), severity: 'error' });
    }
  };

  const updateInstallment = async (inst) => {
    if (!activeStructure) return;
    try {
      const payload = {
        name: inst.name,
        sequence: Number(inst.sequence),
        amount: Number(inst.amount || 0),
        dueAt: inst.dueAt,
        isMandatory: !!inst.isMandatory
      };
      await fetchJson(`/api/finance/fee-structures/${activeStructure.id}/installments/${inst.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      setSnackbar({ open: true, message: t('feeStructure.snack.installmentUpdated'), severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: error?.message || t('feeStructure.errors.updateInstallment'), severity: 'error' });
    }
  };

  const deleteInstallment = async (inst) => {
    if (!activeStructure) return;
    try {
      await fetchJson(`/api/finance/fee-structures/${activeStructure.id}/installments/${inst.id}`, {
        method: 'DELETE'
      });
      setSnackbar({ open: true, message: t('feeStructure.snack.installmentDeleted'), severity: 'success' });
      // remove from local list
      setInstallments(prev => prev.filter(i => i.id !== inst.id));
    } catch (error) {
      setSnackbar({ open: true, message: error?.message || t('feeStructure.errors.deleteInstallment'), severity: 'error' });
    }
  };

  // ===== Installment utilities: totals and equal distribution =====
  const sumInstallments = (list) => (list || []).reduce((s, i) => s + Number(i.amount || 0), 0);
  const distributeEqual = (total, count) => {
    const t = Number(total || 0);
    const n = Math.max(1, Number(count || 1));
    const base = Math.floor((t / n) * 100) / 100; // floor to 2dp
    const amounts = Array(n).fill(base);
    const rem = Math.round((t - base * n) * 100) / 100; // remaining cents
    amounts[n - 1] = Math.round((amounts[n - 1] + rem) * 100) / 100;
    return amounts;
  };

  const rebalanceExistingInstallments = () => {
    if (!activeStructure) return;
    const total = Number(activeStructure.amount || 0);
    setInstallments(prev => {
      const n = prev.length;
      if (n === 0) return prev;
      const amounts = distributeEqual(total, n);
      return prev
        .sort((a, b) => Number(a.sequence) - Number(b.sequence))
        .map((inst, idx) => ({ ...inst, sequence: idx + 1, amount: amounts[idx] }));
    });
    setSnackbar({ open: true, message: t('feeStructure.snack.amountsRebalanced'), severity: 'info' });
  };

  // Persist all edits (create/update/delete) in bulk
  const saveAllInstallments = async () => {
    if (!activeStructure) return;
    try {
      const desired = installments
        .slice()
        .sort((a, b) => Number(a.sequence) - Number(b.sequence))
        .map((x, idx) => ({ ...x, sequence: idx + 1 }));

      const toCreate = desired.filter(i => !i.id || String(i.id).startsWith('temp')).map(i => ({
        name: i.name,
        sequence: i.sequence,
        amount: Number(i.amount || 0),
        dueAt: i.dueAt || '',
        isMandatory: !!i.isMandatory
      }));

      const toUpdate = desired.filter(i => i.id && !String(i.id).startsWith('temp')).map(i => ({
        id: i.id,
        payload: {
          name: i.name,
          sequence: i.sequence,
          amount: Number(i.amount || 0),
          dueAt: i.dueAt || '',
          isMandatory: !!i.isMandatory
        }
      }));

      const toDelete = (originalInstallments || []).filter(oi => !desired.some(i => i.id === oi.id));

      // Execute requests
      if (toCreate.length > 0) {
        await fetchJson(`/api/finance/fee-structures/${activeStructure.id}/installments`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: toCreate })
        });
      }
      await Promise.all(
        toUpdate.map(u =>
          fetchJson(`/api/finance/fee-structures/${activeStructure.id}/installments/${u.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(u.payload)
          })
        )
      );
      await Promise.all(
        toDelete.map(d =>
          fetchJson(`/api/finance/fee-structures/${activeStructure.id}/installments/${d.id}`, { method: 'DELETE' })
        )
      );

      const res = await fetchJson(`/api/finance/fee-structures/${activeStructure.id}/installments`, { method: 'GET' });
      const list = res.data || res.installments || [];
      setInstallments(list);
      setOriginalInstallments(list);
      setInstCount(Math.max(1, list.length || 1));
      setSnackbar({ open: true, message: t('feeStructure.snack.allInstallmentsSaved'), severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: error?.message || t('feeStructure.errors.saveInstallments'), severity: 'error' });
    }
  };

  const generateEqualInstallments = async () => {
    if (!activeStructure) return;
    const total = Number(activeStructure.amount || 0);
    const n = Math.max(1, Number(genCount || 1));
    const amounts = distributeEqual(total, n);
    const items = amounts.map((amt, idx) => ({
      name: t('feeStructure.installments.defaultName', { n: idx + 1 }),
      sequence: idx + 1,
      amount: amt,
      dueAt: '',
      isMandatory: true
    }));
    try {
      await fetchJson(`/api/finance/fee-structures/${activeStructure.id}/installments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });
      const res = await fetchJson(`/api/finance/fee-structures/${activeStructure.id}/installments`, { method: 'GET' });
      setInstallments(res.data || res.installments || []);
      setSnackbar({ open: true, message: t('feeStructure.snack.equalGenerated'), severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: error?.message || t('feeStructure.errors.generateInstallments'), severity: 'error' });
    }
  };

  useEffect(() => {
    // 2s guard: ensure we don't show an endless loader when no cache
    if ((feeStructures || []).length === 0) {
      guardRef.current = setTimeout(() => setLoading(false), 2000);
    }
    fetchData();
    return () => { if (guardRef.current) clearTimeout(guardRef.current); };
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [structuresRes, classesRes, feeTypesRes, academicYearsRes] = await Promise.all([
        fetchJson('/api/finance/fee-structures', { method: 'GET' }),
        fetchJson('/api/classes/for-allocations', { method: 'GET' }),
        fetchJson('/api/fee-types', { method: 'GET' }),
        fetchJson('/api/school-academic-years', { method: 'GET' }).catch(async (err) => {
          console.warn('Failed to load school academic years, trying global:', err);
          // If school academic years fail, try global academic years as fallback
          return fetchJson('/api/global-academic-years', { method: 'GET' });
        })
      ]);

      const structures = structuresRes.data || structuresRes.structures || [];
      const cls = Array.isArray(classesRes) ? classesRes : (classesRes.classes || classesRes.data || []);
      const feeTypesList = Array.isArray(feeTypesRes) ? feeTypesRes : (feeTypesRes.data || feeTypesRes.feeTypes || []);
      // The school academic years endpoint returns SchoolAcademicYear objects with nested globalYear
      // We need to extract the global year data from each school academic year
      let ayList = [];
      if (Array.isArray(academicYearsRes)) {
        if (academicYearsRes.length > 0 && academicYearsRes[0].globalYear) {
          // This is school academic years with nested global years
          ayList = academicYearsRes.map(say => ({
            id: say.id, // Use the school academic year ID for the value
            name: say.globalYear?.name || say.name,
            year: say.globalYear?.year || say.year,
            startDate: say.localStartsAt || say.globalYear?.startsAt || say.startDate,
            endDate: say.localEndsAt || say.globalYear?.endsAt || say.endDate,
            isCurrent: say.globalYear?.isCurrent || say.isCurrent,
            globalYearId: say.globalAcademicYearId,
            startsAt: say.globalYear?.startsAt,
            endsAt: say.globalYear?.endsAt
          }));
        } else if (academicYearsRes.length > 0) {
          // This might be global academic years directly (fallback case)
          ayList = academicYearsRes.map(ay => ({
            id: ay.id,
            name: ay.name,
            year: ay.year || ay.name,
            startDate: ay.startsAt || ay.startDate,
            endDate: ay.endsAt || ay.endDate,
            isCurrent: ay.isCurrent,
            startsAt: ay.startsAt,
            endsAt: ay.endsAt
          }));
        }
      } else if (academicYearsRes?.data) {
        ayList = academicYearsRes.data;
      }

      setFeeStructures(structures || []);
      setClasses(cls || []);
      setFeeTypes(feeTypesList || []);
      setAcademicYears(ayList || []);
      
      // Find and set current academic year
      // The academicYearId from localStorage should match the id from the database
      const currentAY = ayList.find(ay => String(ay.id) === String(ayId)) || 
                       ayList.find(ay => String(ay.globalYearId) === String(ayId)) ||
                       ayList.find(ay => ay.isActive || ay.isCurrent) || 
                       ayList[0];
      if (currentAY) {
        setCurrentAcademicYear(currentAY);
      }

      // Check if fee types is empty and provide default
      if (!feeTypesList || feeTypesList.length === 0) {
        console.warn('No fee types found, you may need to create fee types first');
      }

      // Persist to cache by context key
      try {
        localStorage.setItem(cacheKeyStructures, JSON.stringify(structures || []));
        localStorage.setItem(cacheKeyClasses, JSON.stringify(cls || []));
        localStorage.setItem(cacheKeyFeeTypes, JSON.stringify(feeTypesList || []));
      } catch (_) {}
    } catch (error) {
      console.error('Error fetching fee structure data:', error);
      console.error('Error details:', error.data || error.response || error.message);
      setSnackbar({ open: true, message: t('feeStructure.errors.loadData'), severity: 'error' });
      // Set empty arrays as fallback
      setFeeStructures([]);
      setClasses([]);
      setFeeTypes([]);
      setAcademicYears([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let successful = [];
    let failed = [];
    try {
      // Validate required fields
      if (!formData.classIds || formData.classIds.length === 0) {
        setSnackbar({ open: true, message: t('feeStructure.errors.selectClass'), severity: 'warning' });
        return;
      }
      
      if (!formData.feeTypeId) {
        setSnackbar({ open: true, message: t('feeStructure.errors.selectFeeType'), severity: 'warning' });
        return;
      }
      
      if (!formData.amount || Number(formData.amount) <= 0) {
        setSnackbar({ open: true, message: t('feeStructure.errors.validAmount'), severity: 'warning' });
        return;
      }
      
      if (!formData.dueDate) {
        setSnackbar({ open: true, message: t('feeStructure.errors.selectDueDate'), severity: 'warning' });
        return;
      }
      
      const endpoint = editingStructure 
        ? `/api/finance/fee-structures/${editingStructure.id}`
        : '/api/finance/fee-structures';
      const base = {
        description: formData.description,
        feeTypeId: formData.feeTypeId,
        academicYearId: formData.academicYearId ? Number(formData.academicYearId) : 1,  // Use academicYearId directly
        phaseId: formData.phaseId ? Number(formData.phaseId) : 1,  // Include phaseId
        amount: Number(formData.amount || 0),
        dueDate: formData.dueDate,
        lateFee: Number(formData.lateFee || 0),
        discount: Number(formData.discount || 0),
        isCompulsory: !!formData.isCompulsory,
        hasInstallments: !!formData.installment,  // Map installment to hasInstallments
        status: formData.status || 'active',
        schoolId: parseInt(localStorage.getItem('schoolId')) || 1  // Include schoolId explicitly as number
      };
      // If installments are enabled, validate amounts equal total
      if (formData.installment) {
        const sum = sumInstallments(inlineInstallments);
        const total = Number(base.amount || 0);
        if (Math.abs(sum - total) >= 0.01) {
          setSnackbar({ open: true, message: t('feeStructure.errors.installmentsMismatch'), severity: 'warning' });
          return;
        }
      }
      let supersededFrom = null;
      let effectiveFrom = null;

      if (editingStructure) {
        // When editing, if multiple classes are selected, update the current one and create duplicates for others
        const selectedClassIds = Array.isArray(formData.classIds) && formData.classIds.length > 0
          ? formData.classIds
          : [editingStructure.classId];
        
        // Update the original structure with first selected class
        const classId = selectedClassIds[0];
        const updateRes = await fetchJson(endpoint, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...base, classId }) });

        // EDITING A FEE THAT STARTED IN AN EARLIER YEAR WRITES A NEW VERSION.
        //
        // The server leaves the old row alone so last year's bills stay true,
        // and returns the new version's id in `data`. Everything that follows —
        // instalments above all — has to be written against THAT row, or the
        // schedule lands on the version nobody is being charged under.
        const savedStructure = updateRes?.data || updateRes?.structure || updateRes;
        const savedId = savedStructure?.id || editingStructure.id;
        supersededFrom = updateRes?.supersededFrom || null;
        effectiveFrom = updateRes?.effectiveFrom || null;

        // If more than one class selected, create duplicates for the rest
        let createdStructureIds = [];
        if (selectedClassIds.length > 1) {
          const duplicatePromises = selectedClassIds.slice(1).map(cid => 
            fetchJson('/api/finance/fee-structures', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ ...base, classId: cid })
            })
          );
          const createdStructures = await Promise.all(duplicatePromises);
          createdStructureIds = createdStructures.map(resp => {
            const s = resp?.data || resp?.structure || resp;
            return s?.id;
          }).filter(Boolean);
        }
        // Persist inline installments if enabled
        if (formData.installment) {
          const desired = inlineInstallments
            .slice()
            .sort((a, b) => Number(a.sequence) - Number(b.sequence))
            .map((x, idx) => ({ ...x, sequence: idx + 1 }));
          const toCreate = desired.filter(i => !i.id || String(i.id).startsWith('temp')).map(i => ({
            name: i.name,
            sequence: i.sequence,
            amount: Number(i.amount || 0),
            dueAt: i.dueAt || '',
            isMandatory: !!i.isMandatory
          }));
          const toUpdate = desired.filter(i => i.id && !String(i.id).startsWith('temp')).map(i => ({ id: i.id, payload: {
            name: i.name,
            sequence: i.sequence,
            amount: Number(i.amount || 0),
            dueAt: i.dueAt || '',
            isMandatory: !!i.isMandatory
          }}));
          const toDelete = (inlineOriginalInstallments || []).filter(oi => !desired.some(i => i.id === oi.id));

          if (supersededFrom) {
            // A new version carries a fresh copy of the old schedule, so the
            // edits the admin just made are applied to that copy wholesale
            // rather than diffed against ids that belong to last year's row.
            const existing = await fetchJson(`/api/finance/fee-structures/${savedId}/installments`, { method: 'GET' });
            const carried = Array.isArray(existing?.data) ? existing.data : [];
            await Promise.all(carried.map(c => fetchJson(`/api/finance/fee-structures/${savedId}/installments/${c.id}`, { method: 'DELETE' })));
            await fetchJson(`/api/finance/fee-structures/${savedId}/installments`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: desired.map((i, idx) => ({
                name: i.name,
                sequence: idx + 1,
                amount: Number(i.amount || 0),
                dueAt: i.dueAt || '',
                isMandatory: !!i.isMandatory,
              })) }),
            });
          } else {
            if (toCreate.length > 0) {
              await fetchJson(`/api/finance/fee-structures/${savedId}/installments`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: toCreate })
              });
            }
            await Promise.all(toUpdate.map(u => fetchJson(`/api/finance/fee-structures/${savedId}/installments/${u.id}`, {
              method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(u.payload)
            })));
            await Promise.all(toDelete.map(d => fetchJson(`/api/finance/fee-structures/${savedId}/installments/${d.id}`, { method: 'DELETE' })));
          }
          
          // If we created duplicates, copy installments to them as well
          if (createdStructureIds && createdStructureIds.length > 0) {
            const installmentItems = desired.map(i => ({
              name: i.name,
              sequence: i.sequence,
              amount: Number(i.amount || 0),
              dueAt: i.dueAt || '',
              isMandatory: !!i.isMandatory
            }));
            
            await Promise.all(createdStructureIds.map(structureId => 
              fetchJson(`/api/finance/fee-structures/${structureId}/installments`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items: installmentItems })
              })
            ));
          }
        }
      } else {
        const ids = Array.isArray(formData.classIds) && formData.classIds.length > 0
          ? formData.classIds
          : [];
        if (ids.length === 0) throw new Error(t('feeStructure.errors.selectClass'));
        
        // Create fee structures for each selected class
        const created = [];
        const errors = [];
        
        for (const classId of ids) {
          try {
            const response = await fetchJson(endpoint, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ ...base, classId })
            });
            created.push(response);
            successful.push({ success: true, classId, data: response });
          } catch (error) {
            console.error('Failed to create for class ID:', classId, error);
            errors.push({ classId, error });
            failed.push({ success: false, classId, error });
            // Continue trying other classes
          }
        }
        
        if (created.length === 0) {
          // All failed - show specific error from first failure
          const firstError = errors[0]?.error;
          const firstMsg =
            (firstError && firstError.data && (firstError.data.message || firstError.data.error))
            || (firstError && firstError.message)
            || '';
          if (String(firstMsg).includes('already exists')) {
            throw new Error(t('feeStructure.errors.duplicateExists'));
          }
          throw new Error(t('feeStructure.errors.couldNotCreate'));
        }
        // After creation, persist installments for each created structure
        if (formData.installment) {
          const items = inlineInstallments
            .slice()
            .sort((a, b) => Number(a.sequence) - Number(b.sequence))
            .map((x, idx) => ({
              name: x.name || t('feeStructure.installments.defaultName', { n: idx + 1 }),
              sequence: idx + 1,
              amount: Number(x.amount || 0),
              dueAt: x.dueAt || '',
              isMandatory: !!x.isMandatory
            }));
          await Promise.all(
            created.map(resp => {
              const s = resp?.data || resp?.structure || resp;
              const sid = s?.id;
              if (!sid) return Promise.resolve();
              return fetchJson(`/api/finance/fee-structures/${sid}/installments`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items })
              });
            })
          );
        }
      }
      // Show appropriate success message
      let successMessage = '';
      if (editingStructure) {
        const classCount = Array.isArray(formData.classIds) ? formData.classIds.length : 1;
        if (classCount > 1) {
          successMessage = t('feeStructure.snack.updatedDuplicated', { count: classCount - 1 });
        } else {
          successMessage = t('feeStructure.snack.updated');
        }
        if (supersededFrom) {
          // This fee had been carried forward from an earlier year, so the edit
          // opened a new version instead of overwriting what was already billed.
          const yearName = academicYears.find(y => Number(y.id) === Number(effectiveFrom))?.name;
          successMessage = yearName
            ? t('feeStructure.snack.effectiveFrom', { year: yearName })
            : t('feeStructure.snack.effectiveFromCurrentYear');
        }
      } else {
        // For creation, check if we had any failures
        if (typeof successful !== 'undefined' && typeof failed !== 'undefined') {
          if (successful.length > 0 && failed.length > 0) {
            // Some succeeded, some failed
            const failedClasses = failed.map(f => {
              const cls = classes.find(c => c.id === f.classId);
              return cls?.name || f.classId;
            }).join(', ');
            successMessage = t('feeStructure.snack.createdPartial', { success: successful.length, failed: failed.length, classes: failedClasses });
          } else if (successful.length > 0) {
            // All succeeded
            successMessage = successful.length > 1 
              ? t('feeStructure.snack.createdMultiple', { count: successful.length })
              : t('feeStructure.snack.created');
          }
        } else {
          // Fallback for edit mode or single creation
          const classCount = Array.isArray(formData.classIds) ? formData.classIds.length : 0;
          if (classCount > 1) {
            successMessage = t('feeStructure.snack.createdMultiple', { count: classCount });
          } else {
            successMessage = t('feeStructure.snack.created');
          }
        }
      }
      setSnackbar({ open: true, message: successMessage, severity: 'success' });
      resetForm();
      // Refresh the table immediately
      await fetchData();
    } catch (error) {
      console.error('Error saving fee structure - Full error:', error);
      console.error('Error data:', error.data);
      console.error('Error message:', error.message);
      
      const rawMessage =
        (error && error.data && (typeof error.data === 'object') && (error.data.message || error.data.error))
        || (error && error.data && (typeof error.data === 'string') && error.data)
        || (error && error.message)
        || '';

      let errorMessage = t('feeStructure.errors.saveStructure');
      if (String(rawMessage).includes('already exists')) {
        errorMessage = t('feeStructure.errors.duplicateExists');
      }
      
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  const resetForm = () => {
    setFormData({
      description: '',
      classIds: [],
      feeTypeId: '',
      academicYearId: currentAcademicYear?.id || ayId || '',
      phaseId: localStorage.getItem('phaseId') || '1',  // Reset phaseId
      amount: '',
      dueDate: '',
      lateFee: '',
      discount: '',
      isCompulsory: false,
      installment: false,
      installmentCount: 1,
      status: 'active'
    });
    setEditingStructure(null);
    setShowForm(false);
    // Reset inline installments
    setInlineInstallments([]);
    setInlineOriginalInstallments([]);
    setInlineInstCount(1);
  };

  const handleEdit = (structure) => {
    setEditingStructure(structure);
    setFormData({
      description: structure.description || '',
      classIds: [structure.classId].filter(Boolean),
      feeTypeId: structure.feeTypeId || '',
      academicYearId: structure.academicYearId || currentAcademicYear?.id || ayId || '',
      phaseId: structure.phaseId || localStorage.getItem('phaseId') || '1',
      amount: structure.amount || '',
      dueDate: structure.dueDate ? structure.dueDate.substring(0,10) : '',
      lateFee: structure.lateFee || '',
      discount: structure.discount || '',
      isCompulsory: !!structure.isCompulsory,
      installment: structure.hasInstallments || false,  // Map hasInstallments to installment
      installmentCount: 1,
      status: structure.status || 'active'
    });
    setShowForm(true);
    // Load installments for inline editor
    (async () => {
      try {
        const res = await fetchJson(`/api/finance/fee-structures/${structure.id}/installments`, { method: 'GET' });
        const list = res.data || res.installments || [];
        if ((list || []).length === 0) {
          const seeded = [{ id: 'temp-1', name: t('feeStructure.installments.defaultName', { n: 1 }), sequence: 1, amount: 0, dueAt: '', isMandatory: true }];
          setInlineInstallments(seeded);
          setInlineOriginalInstallments([]);
          setInlineInstCount(1);
        } else {
          setInlineInstallments(list);
          setInlineOriginalInstallments(list);
          setInlineInstCount(Math.max(1, list.length || 1));
        }
      } catch (e) {
        setSnackbar({ open: true, message: t('feeStructure.errors.loadInstallmentsEdit'), severity: 'error' });
      }
    })();
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetchJson(`/api/finance/fee-structures/${id}`, { method: 'DELETE' });
      // A fee that has already been billed in an earlier year is discontinued
      // from this year rather than erased, so the message has to match what
      // actually happened — the row is still there under last year.
      setSnackbar({
        open: true,
        message: res?.discontinuedFrom
          ? t('feeStructure.snack.discontinued')
          : t('feeStructure.snack.deleted'),
        severity: 'success',
      });
      fetchData();
    } catch (error) {
      setSnackbar({ open: true, message: t('feeStructure.errors.deleteStructure'), severity: 'error' });
    }
  };

  // legacy badge styles removed; using MUI Chip below

  const classFeeStructures = useMemo(() => {
    if (!printClassId) return [];
    return (feeStructures || [])
      .filter(fs => String(fs.classId) === String(printClassId))
      .sort((a, b) => String(a?.FeeType?.name || a?.name || '').localeCompare(String(b?.FeeType?.name || b?.name || '')));
  }, [feeStructures, printClassId]);

  const printSelectedClass = () => {
    const className = classes.find(c => String(c.id) === String(printClassId))?.name || '-';
    const rows = classFeeStructures;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const currency = 'XAF';
    const html = `
      <h2 style="margin:0 0 8px 0;">${t('feeStructure.print.printTitle')}</h2>
      <div style="color:#6b7280;font-size:12px;margin-bottom:14px;">
        ${t('feeStructure.print.printClass', { name: className })}<br/>
        ${t('feeStructure.print.printTotal', { count: rows.length })}
      </div>
      <table style="width:100%;border-collapse:collapse;margin-bottom:16px;">
        <thead>
          <tr>
            <th style="border:1px solid #d1d5db;padding:8px;text-align:left;background:#f3f4f6;">${t('feeStructure.print.printFee')}</th>
            <th style="border:1px solid #d1d5db;padding:8px;text-align:right;background:#f3f4f6;">${t('feeStructure.print.printAmount')}</th>
            <th style="border:1px solid #d1d5db;padding:8px;text-align:left;background:#f3f4f6;">${t('feeStructure.print.printDueDate')}</th>
            <th style="border:1px solid #d1d5db;padding:8px;text-align:left;background:#f3f4f6;">${t('feeStructure.print.printStatus')}</th>
            <th style="border:1px solid #d1d5db;padding:8px;text-align:left;background:#f3f4f6;">${t('feeStructure.print.printInstallments')}</th>
          </tr>
        </thead>
        <tbody>
          ${rows.map(fs => {
            const amount = Number(fs.amount || 0);
            const due = fs.dueDate ? new Date(fs.dueDate).toLocaleDateString() : '-';
            const inst = Array.isArray(fs.installments) ? [...fs.installments].sort((a,b)=>Number(a.sequence||0)-Number(b.sequence||0)) : [];
            const instHtml = inst.length
              ? `<div style=\"font-size:12px;\">${inst.map(i => `${i.sequence}. ${i.name} - ${Number(i.amount||0).toLocaleString()} ${currency}${i.dueAt ? ` (${t('feeStructure.print.printDue', { date: new Date(i.dueAt).toLocaleDateString() })})` : ''}`).join('<br/>')}</div>`
              : '-';
            const feeName = fs.FeeType?.name || fs.name || t('feeStructure.print.printFee');
            return `
              <tr>
                <td style="border:1px solid #d1d5db;padding:8px;">${feeName}</td>
                <td style="border:1px solid #d1d5db;padding:8px;text-align:right;">${amount.toLocaleString()} ${currency}</td>
                <td style="border:1px solid #d1d5db;padding:8px;">${due}</td>
                <td style="border:1px solid #d1d5db;padding:8px;">${fs.status || '-'}</td>
                <td style="border:1px solid #d1d5db;padding:8px;">${instHtml}</td>
              </tr>
            `;
          }).join('')}
        </tbody>
      </table>
    `;

    printWindow.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>${t('feeStructure.print.printTitle')} - ${className}</title><style>body{font-family:Arial,sans-serif;padding:20px;}h2{font-size:18px;}</style></head><body>${html}</body></html>`);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 250);
  };

  if (loading && (feeStructures || []).length === 0) {
    return <AppLoader variant="circular3" size={48} centered messages={[t('feeStructure.loading')]} />;
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-2">
        <h1 className="text-2xl font-bold">{t('feeStructure.title')}</h1>
        <Button onClick={() => {
          resetForm();
          setShowForm(true);
        }}>{t('feeStructure.addStructure')}</Button>
        <Dialog open={showForm} onClose={() => setShowForm(false)} maxWidth="lg" fullWidth>
          <DialogTitle>{editingStructure ? t('feeStructure.editStructure') : t('feeStructure.addStructure')}</DialogTitle>
          <DialogContent>
            <form id="fee-structure-form" onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormControl fullWidth size="medium">
                  <InputLabel id="classes-label">
                    {editingStructure ? t('feeStructure.dialog.applyToClasses') : t('feeStructure.dialog.selectClasses')}
                  </InputLabel>
                  <Select
                    labelId="classes-label"
                    label={editingStructure ? t('feeStructure.dialog.applyToClasses') : t('feeStructure.dialog.selectClasses')}
                    multiple
                    value={formData.classIds}
                    onChange={(e) => setFormData({ ...formData, classIds: e.target.value })}
                    renderValue={(selected) => {
                      if (!selected || selected.length === 0) {
                        return <em style={{ color: '#999' }}>{t('feeStructure.dialog.selectClassesPlaceholder')}</em>;
                      }
                      const map = new Map(classes.map(c => [c.id, c.name]));
                      return (selected || []).map(id => map.get(id) || id).join(', ');
                    }}
                    required
                  >
                    {classes.length > 0 ? (
                      classes.map(cl => (
                        <MenuItem key={cl.id} value={cl.id}>
                          <Checkbox checked={formData.classIds.indexOf(cl.id) > -1} />
                          {cl.name}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>{t('feeStructure.dialog.noClasses')}</MenuItem>
                    )}
                  </Select>
                  {editingStructure && (
                    <FormHelperText>
                      {t('feeStructure.dialog.duplicateHelper')}
                    </FormHelperText>
                  )}
                </FormControl>

                <FormControl fullWidth size="medium">
                  <InputLabel id="fee-type-label">{t('feeStructure.dialog.feeType')}</InputLabel>
                  <Select
                    labelId="fee-type-label"
                    label={t('feeStructure.dialog.feeType')}
                    value={formData.feeTypeId}
                    onChange={(e) => setFormData({ ...formData, feeTypeId: e.target.value })}
                  >
                    {feeTypes.length > 0 ? (
                      feeTypes.map(ft => (
                        <MenuItem key={ft.id} value={ft.id}>{ft.name}</MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>{t('feeStructure.dialog.noFeeTypes')}</MenuItem>
                    )}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="medium">
                  <InputLabel id="academic-year-label">{t('feeStructure.dialog.academicYear')}</InputLabel>
                  <Select
                    labelId="academic-year-label"
                    label={t('feeStructure.dialog.academicYear')}
                    value={formData.academicYearId}
                    onChange={(e) => setFormData({ ...formData, academicYearId: e.target.value })}
                  >
                    {academicYears.length > 0 ? (
                      academicYears.map(ay => (
                        <MenuItem key={ay.id} value={ay.id}>
                          {ay.name || ay.year || `${ay.startYear || ay.startDate?.substring(0,4)}-${ay.endYear || ay.endDate?.substring(0,4)}`}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>{t('feeStructure.dialog.noAcademicYears')}</MenuItem>
                    )}
                  </Select>
                </FormControl>

                <TextField
                  label={t('feeStructure.dialog.amount')}
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0.00"
                  required
                  size="medium"
                  fullWidth
                />

                <TextField
                  label={t('feeStructure.dialog.dueDate')}
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                  required
                  size="medium"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />

                <TextField
                  label={t('feeStructure.dialog.lateFee')}
                  type="number"
                  value={formData.lateFee}
                  onChange={(e) => setFormData({ ...formData, lateFee: e.target.value })}
                  placeholder="0.00"
                  size="medium"
                  fullWidth
                />
              </div>

              <TextField
                label={t('feeStructure.dialog.description')}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t('feeStructure.dialog.descriptionPlaceholder')}
                rows={3}
                multiline
                fullWidth
                size="medium"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextField
                  label={t('feeStructure.dialog.discount')}
                  type="number"
                  value={formData.discount}
                  onChange={(e) => setFormData({ ...formData, discount: e.target.value })}
                  placeholder="0"
                  size="medium"
                  fullWidth
                />

                <FormControl fullWidth size="medium">
                  <InputLabel id="status-label">{t('feeStructure.dialog.statusLabel')}</InputLabel>
                  <Select
                    labelId="status-label"
                    label={t('feeStructure.dialog.statusLabel')}
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <MenuItem value="active">{t('feeStructure.dialog.statusActive')}</MenuItem>
                    <MenuItem value="inactive">{t('feeStructure.dialog.statusInactive')}</MenuItem>
                    <MenuItem value="archived">{t('feeStructure.dialog.statusArchived')}</MenuItem>
                  </Select>
                </FormControl>
              </div>

              {/* Payment Gateways removed: all gateways are valid for all payments */}

              <FormControlLabel
                control={<Checkbox checked={!!formData.isCompulsory} onChange={(e) => setFormData({ ...formData, isCompulsory: e.target.checked })} />}
                label={t('feeStructure.dialog.compulsory')}
              />

              <FormControlLabel
                control={<Checkbox checked={formData.installment} onChange={(e) => setFormData({ ...formData, installment: e.target.checked })} />}
                label={t('feeStructure.dialog.enableInstallments')}
              />

              {formData.installment && (
                <>
                  <TextField
                    label={t('feeStructure.dialog.installmentCount')}
                    type="number"
                    value={formData.installmentCount}
                    onChange={(e) => setFormData({ ...formData, installmentCount: e.target.value })}
                    inputProps={{ min: 1, max: 24 }}
                  />
                  <div className="space-y-3 border-t pt-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <Chip label={t('feeStructure.installments.structureAmount', { amount: formatXAF(Number(formData.amount || editingStructure?.amount || 0)) })} color="primary" variant="outlined" />
                    {(() => {
                      const sum = sumInstallments(inlineInstallments);
                      const total = Number(formData.amount || editingStructure?.amount || 0);
                      const diff = Math.round((sum - total) * 100) / 100;
                      const ok = Math.abs(diff) < 0.01;
                      const suffix = ok ? t('feeStructure.installments.ok') : diff > 0 ? t('feeStructure.installments.overBy', { amount: formatXAF(diff) }) : t('feeStructure.installments.underBy', { amount: formatXAF(Math.abs(diff)) });
                      return (
                        <Chip
                          label={`${t('feeStructure.installments.installmentsTotal', { amount: formatXAF(sum) })} ${suffix}`}
                          color={ok ? 'success' : 'warning'}
                          variant={ok ? 'filled' : 'outlined'}
                        />
                      );
                    })()}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <TextField
                      label={t('feeStructure.installments.numberOfInstallments')}
                      type="number"
                      size="small"
                      value={inlineInstCount}
                      onChange={(e) => handleInlineInstCountChange(e.target.value)}
                      inputProps={{ min: 1, max: 24 }}
                    />
                    <Button variant="outlined" onClick={inlineRebalance}>{t('feeStructure.installments.rebalance')}</Button>
                    <Button variant="outlined" onClick={inlineAutoDistributeDueDates}>{t('feeStructure.installments.autoDistribute')}</Button>
                    {editingStructure && (
                      <Button variant="contained" color="primary" onClick={inlineSaveAll}>{t('feeStructure.installments.saveAll')}</Button>
                    )}
                  </div>
                  <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 720 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('feeStructure.installments.hashCol')}</TableCell>
                          <TableCell>{t('feeStructure.installments.name')}</TableCell>
                          <TableCell>{t('feeStructure.installments.amountCol')}</TableCell>
                          <TableCell>{t('feeStructure.installments.dueCol')}</TableCell>
                          <TableCell>{t('feeStructure.installments.mandatory')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {inlineInstallments.map(inst => (
                        <TableRow key={inst.id}>
                          <TableCell>{inst.sequence}</TableCell>
                          <TableCell>
                            <TextField variant="standard" size="small" value={inst.name} onChange={(e) => setInlineInstallments(prev => prev.map(i => i.id === inst.id ? { ...i, name: e.target.value } : i))} />
                          </TableCell>
                          <TableCell>
                            <TextField variant="standard" size="small" type="number" value={inst.amount} onChange={(e) => setInlineInstallments(prev => prev.map(i => i.id === inst.id ? { ...i, amount: e.target.value } : i))} />
                          </TableCell>
                          <TableCell>
                            <TextField variant="standard" size="small" type="datetime-local" value={inst.dueAt ? String(inst.dueAt).substring(0,16) : ''} onChange={(e) => setInlineInstallments(prev => prev.map(i => i.id === inst.id ? { ...i, dueAt: e.target.value } : i))} />
                          </TableCell>
                          <TableCell>
                            <Checkbox checked={!!inst.isMandatory} onChange={(e) => setInlineInstallments(prev => prev.map(i => i.id === inst.id ? { ...i, isMandatory: e.target.checked } : i))} />
                          </TableCell>
                        </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
                </>
              )}
            </form>
          </DialogContent>
          <DialogActions>
            <Button onClick={resetForm} color="inherit">{t('feeStructure.dialog.cancel')}</Button>
            <Button type="submit" form="fee-structure-form" variant="contained">{editingStructure ? t('feeStructure.dialog.updateStructure') : t('feeStructure.dialog.createStructure')}</Button>
          </DialogActions>
        </Dialog>
      </div>

      <Card sx={{ mb: 2 }}>
        <CardHeader title={t('feeStructure.print.title')} />
        <CardContent>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 240 }}>
              <InputLabel id="print-class-label">{t('feeStructure.print.classLabel')}</InputLabel>
              <Select
                labelId="print-class-label"
                label={t('feeStructure.print.classLabel')}
                value={printClassId}
                onChange={(e) => setPrintClassId(e.target.value)}
              >
                <MenuItem value="">{t('feeStructure.print.selectClass')}</MenuItem>
                {classes.map(c => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              disabled={!printClassId || classFeeStructures.length === 0}
              onClick={printSelectedClass}
            >
              {t('feeStructure.print.printBtn')}
            </Button>
            <div style={{ color: '#6b7280', fontSize: 12 }}>
              {printClassId ? t('feeStructure.print.foundStructures', { count: classFeeStructures.length }) : t('feeStructure.print.selectClassHint')}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader title={t('feeStructure.table.title')} />
        <CardContent>
          {/* Removed legacy "See/Seed Fee Types" button. Manage fee types in Fee Types page. */}
          <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
          <Table sx={{ minWidth: 1100 }}>
            <TableHead>
              <TableRow>
                <TableCell component="th">{t('feeStructure.table.feeType')}</TableCell>
                <TableCell component="th">{t('feeStructure.table.class')}</TableCell>
                <TableCell component="th">{t('feeStructure.table.phase')}</TableCell>
                <TableCell component="th">{t('feeStructure.table.academicYear')}</TableCell>
                <TableCell component="th">{t('feeStructure.table.amount')}</TableCell>
                <TableCell component="th">{t('feeStructure.table.dueDate')}</TableCell>
                <TableCell component="th">{t('feeStructure.table.compulsory')}</TableCell>
                <TableCell component="th">{t('feeStructure.table.installments')}</TableCell>
                <TableCell component="th">{t('feeStructure.table.status')}</TableCell>
                {/* Gateways column removed */}
                <TableCell component="th">{t('feeStructure.table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {feeStructures.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    {loading ? t('feeStructure.table.loadingStructures') : t('feeStructure.table.noStructures')}
                  </TableCell>
                </TableRow>
              ) : null}
              {feeStructures.map((structure) => (
                <TableRow key={structure.id}>
                  <TableCell className="font-medium">{structure.FeeType?.name || structure.feeType?.name || t('feeStructure.na')}</TableCell>
                  <TableCell>{structure.Class?.name || structure.class?.name || structure.classId}</TableCell>
                  <TableCell>{structure.Phase?.name || t('feeStructure.phase', { id: structure.phaseId }) || t('feeStructure.na')}</TableCell>
                  <TableCell>
                    {(() => {
                      // Use GlobalAcademicYear if available, otherwise try to find in our list
                      if (structure.GlobalAcademicYear) {
                        return structure.GlobalAcademicYear.name || `${structure.GlobalAcademicYear.startsAt?.substring(0,4)}-${structure.GlobalAcademicYear.endsAt?.substring(0,4)}`;
                      }
                      const ay = academicYears.find(a => String(a.id) === String(structure.academicYearId));
                      return ay ? (ay.name || ay.year || `${ay.startYear || ay.startDate?.substring(0,4)}-${ay.endYear || ay.endDate?.substring(0,4)}`) : (structure.academicYearId || '—');
                    })()}
                  </TableCell>
                  <TableCell>{formatXAF(structure.amount)}</TableCell>
                  <TableCell>{new Date(structure.dueDate).toLocaleDateString()}</TableCell>
                  <TableCell>
                    {(() => {
                      const hasMandatoryInst = Array.isArray(structure.installments) && structure.installments.some(i => i.isMandatory);
                      const comp = !!structure.isCompulsory || hasMandatoryInst;
                      return <Chip size="small" label={comp ? t('feeStructure.yes') : t('feeStructure.no')} color={comp ? 'warning' : 'default'} />;
                    })()}
                  </TableCell>
                  <TableCell>
                    {Array.isArray(structure.installments) ? structure.installments.length : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={structure.status} color={structure.status === 'active' ? 'success' : structure.status === 'inactive' ? 'default' : 'error'} />
                  </TableCell>
                  {/* Gateways cell removed */}
                  <TableCell>
                    <div className="flex space-x-2">
                      <Button
                        size="small"
                        onClick={() => handleEdit(structure)}
                      >
                        {t('feeStructure.actions.edit')}
                      </Button>
                      <Button size="small" onClick={() => openInstallments(structure)}>{t('feeStructure.actions.installments')}</Button>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleDelete(structure.id)}
                      >
                        {t('feeStructure.actions.delete')}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Installments Dialog */}
      <Dialog open={installmentsOpen} onClose={handleCloseInstallments} maxWidth="md" fullWidth>
        <DialogTitle>{activeStructure ? t('feeStructure.installments.titleWithName', { name: activeStructure.name }) : t('feeStructure.installments.title')}</DialogTitle>
        <DialogContent>
          <div className="space-y-4">
            {activeStructure && (
              <div className="flex flex-wrap items-center gap-2">
                <Chip label={t('feeStructure.installments.structureAmount', { amount: formatXAF(Number(activeStructure.amount || 0)) })} color="primary" variant="outlined" />
                {(() => {
                  const sum = sumInstallments(installments);
                  const total = Number(activeStructure.amount || 0);
                  const diff = Math.round((sum - total) * 100) / 100;
                  const ok = Math.abs(diff) < 0.01;
                  const suffix = ok ? t('feeStructure.installments.ok') : diff > 0 ? t('feeStructure.installments.overBy', { amount: formatXAF(diff) }) : t('feeStructure.installments.underBy', { amount: formatXAF(Math.abs(diff)) });
                  return (
                    <Chip
                      label={`${t('feeStructure.installments.installmentsTotal', { amount: formatXAF(sum) })} ${suffix}`}
                      color={ok ? 'success' : 'warning'}
                      variant={ok ? 'filled' : 'outlined'}
                    />
                  );
                })()}
              </div>
            )}

            {/* Count-driven controls (always visible) */}
            {activeStructure && (
              <div className="flex flex-wrap items-center gap-2">
                <TextField
                  label={t('feeStructure.installments.numberOfInstallments')}
                  type="number"
                  size="small"
                  value={instCount}
                  onChange={(e) => handleInstCountChange(e.target.value)}
                  inputProps={{ min: 1, max: 24 }}
                />
                <Button variant="outlined" onClick={rebalanceExistingInstallments}>{t('feeStructure.installments.rebalance')}</Button>
                <Button variant="outlined" onClick={autoDistributeDueDates}>{t('feeStructure.installments.autoDistribute')}</Button>
                <Button variant="contained" color="primary" onClick={saveAllInstallments}>{t('feeStructure.installments.saveAll')}</Button>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <TextField label={t('feeStructure.installments.name')} size="small" value={instForm.name} onChange={(e) => setInstForm({ ...instForm, name: e.target.value })} />
              <TextField label={t('feeStructure.installments.sequence')} type="number" size="small" value={instForm.sequence} onChange={(e) => setInstForm({ ...instForm, sequence: e.target.value })} />
              <TextField label={t('feeStructure.installments.amountXAF')} type="number" size="small" value={instForm.amount} onChange={(e) => setInstForm({ ...instForm, amount: e.target.value })} />
              <TextField label={t('feeStructure.installments.dueAt')} type="datetime-local" size="small" value={instForm.dueAt} onChange={(e) => setInstForm({ ...instForm, dueAt: e.target.value })} InputLabelProps={{ shrink: true }} />
            </div>
            <FormControlLabel control={<Checkbox checked={instForm.isMandatory} onChange={(e) => setInstForm({ ...instForm, isMandatory: e.target.checked })} />} label={t('feeStructure.installments.mandatory')} />
            <Button variant="contained" onClick={addInstallment}>{t('feeStructure.installments.addInstallment')}</Button>

            <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 860 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('feeStructure.installments.hashCol')}</TableCell>
                    <TableCell>{t('feeStructure.installments.name')}</TableCell>
                    <TableCell>{t('feeStructure.installments.amountCol')}</TableCell>
                    <TableCell>{t('feeStructure.installments.dueCol')}</TableCell>
                    <TableCell>{t('feeStructure.installments.mandatory')}</TableCell>
                    <TableCell>{t('feeStructure.table.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {installments.map(inst => (
                  <TableRow key={inst.id}>
                    <TableCell>{inst.sequence}</TableCell>
                    <TableCell>
                      <TextField variant="standard" size="small" value={inst.name} onChange={(e) => setInstallments(prev => prev.map(i => i.id === inst.id ? { ...i, name: e.target.value } : i))} />
                    </TableCell>
                    <TableCell>
                      <TextField variant="standard" size="small" type="number" value={inst.amount} onChange={(e) => setInstallments(prev => prev.map(i => i.id === inst.id ? { ...i, amount: e.target.value } : i))} />
                    </TableCell>
                    <TableCell>
                      <TextField variant="standard" size="small" type="datetime-local" value={inst.dueAt ? inst.dueAt.substring(0,16) : ''} onChange={(e) => setInstallments(prev => prev.map(i => i.id === inst.id ? { ...i, dueAt: e.target.value } : i))} />
                    </TableCell>
                    <TableCell>
                      <Checkbox checked={!!inst.isMandatory} onChange={(e) => setInstallments(prev => prev.map(i => i.id === inst.id ? { ...i, isMandatory: e.target.checked } : i))} />
                    </TableCell>
                    <TableCell>
                      <Button size="small" onClick={() => updateInstallment(inst)}>{t('feeStructure.installments.save')}</Button>
                      <Button size="small" color="error" onClick={() => deleteInstallment(inst)}>{t('feeStructure.installments.delete')}</Button>
                    </TableCell>
                  </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInstallments}>{t('feeStructure.installments.close')}</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default FeeStructureManagerPage;
